import { QueryClientProvider } from '@tanstack/react-query';
import React from 'react';
import { Provider } from 'react-redux';
import { PostHogProvider } from 'posthog-js/react';

import { queryClient } from '../lib/react-query/react-query';
import store from '../store';

type AppProviderProps = {
  children: React.ReactNode;
};

const ReactQueryDevtools = React.lazy(() =>
  import('@tanstack/react-query-devtools/build/lib/index.prod.js').then(
    (d) => ({
      default: d.ReactQueryDevtools,
    })
  )
);

const isDevelopment =
  process.env.NODE_ENV === 'development' ||
  process.env.NODE_ENV === 'local_dev';

export const AppProvider = ({ children }: AppProviderProps) => {
  const posthogKey = process.env.REACT_APP_PUBLIC_POSTHOG_KEY;
  const posthogHost = process.env.REACT_APP_PUBLIC_POSTHOG_HOST;

  const shouldInitPosthog = !!posthogKey && !!posthogHost;

  if (!posthogKey) {
    console.warn('[AppProvider] Skipping PostHog — key not provided');
  }
  if (!posthogHost) {
    console.warn('[AppProvider] PostHog host not provided');
  }

  const content = (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        {children}
        {isDevelopment && (
          <React.Suspense fallback={null}>
            <ReactQueryDevtools position="bottom-right" />
          </React.Suspense>
        )}
      </QueryClientProvider>
    </Provider>
  );

  // Wrap with PostHogProvider only if fully configured
  if (!shouldInitPosthog) {
    return content;
  }

  return (
    <PostHogProvider
      apiKey={posthogKey}
      options={{
        api_host: posthogHost,
        capture_exceptions: true,
        debug: isDevelopment, // Enable debug mode only in development
      }}
    >
      {content}
    </PostHogProvider>
  );
};
